// Sending text messages (M23, docs/reliable-sending.md): a message shows the
// moment it's sent, goes out in order per chat, and waits on the device when
// the connection is down. Every message carries a clientId, so a retry of a
// send that actually arrived returns the original instead of a copy (D32).
//
// M29 (docs/offline-sending-two.md): the outbox lives in IndexedDB (D66),
// edits and reactions made offline wait in it too (D69), and each entry keeps
// its ready-to-send request so the service worker can send it while Twinden
// is closed (D68).
import { getDefaultStore } from 'jotai';
import { api, ApiError } from './api';
import {
  outboxAtom,
  type OutboxEntry,
  type GatheringDraft,
  type PollDraft,
  type QueuedRequest,
} from '../store';
import type { SentMessage } from '../hooks/useConversationMessages';
import type { ReplySnapshot } from '../components/chat/messageUtils';
import {
  editRequest,
  gatheringRequest,
  pollRequest,
  textRequest,
} from './e2ee/send';
import { forgetScope, setQueuedRevisionSource } from './e2ee/session';
import { t } from '../i18n';
import { countClientFailure } from './errors';
import { isUpdateRequired } from './updateRequired';
import {
  readEntries,
  requestBackgroundSync,
  writeAuth,
  writeEntries,
} from './outboxStore';

const store = getDefaultStore();
const LEGACY_PREFIX = 'twinden:outbox:';

let ownerId: string | null = null;
let loading: Promise<void> = Promise.resolve();
const running = new Set<string>();
const sentListeners = new Set<(message: SentMessage) => void>();
const refusedListeners = new Set<(entry: OutboxEntry) => void>();

function setEntries(update: (entries: OutboxEntry[]) => OutboxEntry[]) {
  const next = update(store.get(outboxAtom));
  store.set(outboxAtom, next);
  if (ownerId) void writeEntries(ownerId, next);
}

function patch(clientId: string, change: Partial<OutboxEntry>) {
  setEntries((entries) =>
    entries.map((entry) =>
      entry.clientId === clientId ? { ...entry, ...change } : entry
    )
  );
}

function remove(clientId: string) {
  setEntries((entries) =>
    entries.filter((entry) => entry.clientId !== clientId)
  );
}

/** Network trouble, a timeout, or a server hiccup: worth trying again (D33). */
export function isRetryable(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  return err.status === 0 || err.status === 429 || err.status >= 500;
}

// Answers that sealing the request again can fix: a newer or new key, an
// edit another edit got ahead of (D326), and a request an older build
// sealed as v1 for a chat that has moved to v2 (426, D328), which is built
// again from the entry as v2.
const RESEALABLE = new Set([
  'STALE_KEY',
  'ROTATION_REQUIRED',
  'STALE_REVISION',
  'UPDATE_REQUIRED',
]);

function resealCode(err: unknown): string | null {
  return err instanceof ApiError && RESEALABLE.has(err.code) ? err.code : null;
}

/** The stored revision a STALE_REVISION answer names, if any. */
function staleRevision(err: unknown): number | undefined {
  const stored =
    err instanceof ApiError && err.code === 'STALE_REVISION'
      ? err.details?.revision
      : undefined;
  return typeof stored === 'number' ? stored : undefined;
}

function buildRequest(
  entry: OutboxEntry,
  atLeastRevision?: number
): Promise<QueuedRequest> {
  const kind = entry.kind ?? 'send';
  if (kind === 'react') {
    const path = `/messages/${entry.messageId}/reaction`;
    // The final state, never a toggle, so a replay can't undo itself (D69).
    return Promise.resolve(
      entry.emoji
        ? { method: 'PUT', path, body: { emoji: entry.emoji, set: true } }
        : { method: 'DELETE', path }
    );
  }
  if (kind === 'edit') {
    return editRequest({
      messageId: entry.messageId!,
      conversationId: entry.conversationId,
      messageClientId: entry.messageClientId,
      atLeastRevision,
      text: entry.content,
      mentions: entry.mentions,
      mentionsEveryone: entry.mentionsEveryone,
    });
  }
  if (entry.poll) {
    return pollRequest({
      conversationId: entry.conversationId,
      clientId: entry.clientId,
      question: entry.content,
      options: entry.poll.options,
      multiple: entry.poll.multiple,
      closesInSeconds: entry.poll.closesInSeconds,
      replyToId: entry.replyToId,
    });
  }
  if (entry.gathering) {
    return gatheringRequest({
      conversationId: entry.conversationId,
      clientId: entry.clientId,
      title: entry.content,
      place: entry.gathering.place,
      note: entry.gathering.note,
      startsAt: entry.gathering.startsAt,
      rings: entry.gathering.rings,
      replyToId: entry.replyToId,
    });
  }
  return textRequest({
    conversationId: entry.conversationId,
    clientId: entry.clientId,
    text: entry.content,
    replyToId: entry.replyToId,
    mentions: entry.mentions,
    mentionsEveryone: entry.mentionsEveryone,
  });
}

// Something that has to wait still gets its request built now, while this
// device has what it needs, so the service worker can send it (D68).
function prepare(clientId: string) {
  const entry = store
    .get(outboxAtom)
    .find((item) => item.clientId === clientId);
  if (!entry || entry.request) return;
  void buildRequest(entry)
    .then((request) => {
      const current = store
        .get(outboxAtom)
        .find((item) => item.clientId === clientId);
      if (current && !current.request && current.status === 'failed') {
        patch(clientId, { request });
        requestBackgroundSync();
      }
    })
    .catch(() => undefined);
}

// Built (and sealed) once and kept, so it can go out as-is later, even from
// the service worker. A chat that moved to a newer key gets it sealed again.
// One reseal per distinct answer, so a rotation followed by an edit that
// got ahead still goes; at most three tries in all.
async function deliver(entry: OutboxEntry) {
  let request = entry.request;
  let atLeastRevision: number | undefined;
  const resealed = new Set<string>();
  for (let attempt = 0; ; attempt += 1) {
    if (!request) {
      request = await buildRequest(entry, atLeastRevision);
      patch(entry.clientId, { request });
    }
    try {
      return await api.sendQueued<{ success: boolean; data: unknown }>(request);
    } catch (err) {
      const code = resealCode(err);
      if (code && !resealed.has(code) && attempt < 2) {
        resealed.add(code);
        forgetScope('chat', entry.conversationId);
        atLeastRevision = staleRevision(err) ?? atLeastRevision;
        request = undefined;
        continue;
      }
      throw err;
    }
  }
}

// Edits waiting here already took their revisions (D326): a new edit of the
// same message goes past them, after a reload too.
setQueuedRevisionSource(() =>
  store.get(outboxAtom).flatMap((entry) => {
    const revision = (entry.request?.body as { revision?: unknown } | undefined)
      ?.revision;
    return (entry.kind ?? 'send') === 'edit' &&
      entry.messageClientId &&
      typeof revision === 'number'
      ? [
          {
            conversationId: entry.conversationId,
            clientId: entry.messageClientId,
            revision,
          },
        ]
      : [];
  })
);

// One worker per chat sends its entries oldest first. A network failure stops
// it and marks the rest as waiting, so messages never arrive out of order.
async function pump(conversationId: string) {
  if (running.has(conversationId)) return;
  running.add(conversationId);
  try {
    for (;;) {
      const entry = store
        .get(outboxAtom)
        .find(
          (item) =>
            item.conversationId === conversationId && item.status === 'sending'
        );
      if (!entry) return;
      try {
        const response = await deliver(entry);
        // Swap the pending row for the real message in one render.
        if ((entry.kind ?? 'send') === 'send') {
          sentListeners.forEach((listener) =>
            listener(response.data as SentMessage)
          );
        }
        remove(entry.clientId);
      } catch (err) {
        if (isUpdateRequired(err)) {
          // An outdated app (#82): this one and everything after it in the
          // chat wait, in order, marked "needs update" — edits and
          // reactions too — and aren't retried until the app is updated.
          setEntries((entries) =>
            entries.map((item) =>
              item.conversationId === conversationId &&
              item.status === 'sending'
                ? {
                    ...item,
                    status: 'failed',
                    rejected: true,
                    needsUpdate: true,
                  }
                : item
            )
          );
          return;
        }
        if (isRetryable(err)) {
          setEntries((entries) =>
            entries.map((item) =>
              item.conversationId === conversationId &&
              item.status === 'sending'
                ? {
                    ...item,
                    status: 'failed',
                    rejected: false,
                    error: undefined,
                  }
                : item
            )
          );
          requestBackgroundSync();
          return;
        }
        if ((entry.kind ?? 'send') !== 'send') {
          // A refused edit or reaction has nothing to retry; the chat shows
          // the server's version again.
          remove(entry.clientId);
          refusedListeners.forEach((listener) => listener(entry));
          continue;
        }
        // A refusal the person sees, counted without any detail (M67, D254).
        countClientFailure('send_failed');
        patch(entry.clientId, {
          status: 'failed',
          rejected: true,
          error:
            err instanceof ApiError ? err.message : t('chat.send.couldNotSend'),
        });
      }
    }
  } finally {
    running.delete(conversationId);
  }
}

function blockedChat(conversationId: string) {
  return store
    .get(outboxAtom)
    .some(
      (entry) =>
        entry.conversationId === conversationId &&
        entry.status === 'failed' &&
        !entry.rejected
    );
}

export function queueMessage(input: {
  conversationId: string;
  content: string;
  replyToId?: string;
  replyTo?: ReplySnapshot | null;
  mentions?: string[];
  mentionsEveryone?: boolean;
  /** A poll (M60, D220); `content` is its question. */
  poll?: PollDraft;
  /** A gathering (M71, D282); `content` is its title. */
  gathering?: GatheringDraft;
}): string {
  const clientId = crypto.randomUUID();
  // Behind a message that's waiting, a new one waits too (order, D33).
  const blocked = blockedChat(input.conversationId);
  setEntries((entries) => [
    ...entries,
    {
      clientId,
      kind: 'send',
      conversationId: input.conversationId,
      content: input.content,
      replyToId: input.replyToId,
      replyTo: input.replyTo ?? null,
      mentions: input.mentions,
      mentionsEveryone: input.mentionsEveryone,
      ...(input.poll ? { poll: input.poll } : {}),
      ...(input.gathering ? { gathering: input.gathering } : {}),
      createdAt: new Date().toISOString(),
      status: blocked ? 'failed' : 'sending',
    },
  ]);
  if (blocked) prepare(clientId);
  else void pump(input.conversationId);
  return clientId;
}

/**
 * An edit or reaction that couldn't reach the server waits here, behind the
 * chat's unsent messages (D69). Only the latest change to a message's text,
 * or to your reaction on it, is kept: that's the state to replay.
 */
export function queueChange(
  change:
    | {
        kind: 'edit';
        conversationId: string;
        messageId: string;
        messageClientId?: string;
        content: string;
        mentions?: string[];
        mentionsEveryone?: boolean;
      }
    | {
        kind: 'react';
        conversationId: string;
        messageId: string;
        emoji: string | null;
      }
) {
  const clientId = crypto.randomUUID();
  setEntries((entries) => [
    ...entries.filter(
      (entry) =>
        !(entry.kind === change.kind && entry.messageId === change.messageId)
    ),
    {
      clientId,
      content: '',
      ...change,
      createdAt: new Date().toISOString(),
      status: 'failed',
    },
  ]);
  prepare(clientId);
}

/** Tap to retry: everything still waiting in that chat goes again, in order. */
export function retryMessage(clientId: string) {
  const target = store
    .get(outboxAtom)
    .find((entry) => entry.clientId === clientId);
  if (!target) return;
  setEntries((entries) =>
    entries.map((entry) =>
      entry.conversationId === target.conversationId &&
      entry.status === 'failed'
        ? {
            ...entry,
            status: 'sending',
            rejected: false,
            needsUpdate: false,
            error: undefined,
          }
        : entry
    )
  );
  void pump(target.conversationId);
}

export function discardMessage(clientId: string) {
  remove(clientId);
}

/** Those chats are gone with their family (M54, D191): nothing waits for them. */
export function discardConversations(conversationIds: string[]) {
  const gone = new Set(conversationIds);
  if (!store.get(outboxAtom).some((entry) => gone.has(entry.conversationId)))
    return;
  setEntries((entries) =>
    entries.filter((entry) => !gone.has(entry.conversationId))
  );
}

/**
 * Those messages disappeared (D238): an edit or reaction still waiting for
 * one of them has nothing to go to.
 */
export function discardForMessages(messageIds: string[]) {
  const gone = new Set(messageIds);
  const stale = (entry: OutboxEntry) =>
    entry.kind !== undefined &&
    entry.kind !== 'send' &&
    Boolean(entry.messageId && gone.has(entry.messageId));
  if (!store.get(outboxAtom).some(stale)) return;
  setEntries((entries) => entries.filter((entry) => !stale(entry)));
}

/**
 * Edits and reactions the service worker found refused: shown as refused
 * here (never dropped unseen), then removed.
 */
function reportRefusedChanges() {
  // Kept until someone can say so: never dropped unseen.
  if (refusedListeners.size === 0) return;
  const refused = store
    .get(outboxAtom)
    .filter((entry) => entry.rejected && (entry.kind ?? 'send') !== 'send');
  for (const entry of refused) {
    remove(entry.clientId);
    refusedListeners.forEach((listener) => listener(entry));
  }
}

/** The connection is back (or the app just opened): send what's waiting. */
export function resumeOutbox() {
  reportRefusedChanges();
  const waiting = store.get(outboxAtom).filter((entry) => !entry.rejected);
  if (waiting.length === 0) return;
  setEntries((entries) =>
    entries.map((entry) =>
      entry.rejected ? entry : { ...entry, status: 'sending' }
    )
  );
  new Set(waiting.map((entry) => entry.conversationId)).forEach(
    (id) => void pump(id)
  );
}

/** Drop entries that did arrive (seen in a loaded chat) (D34). */
export function reconcileOutbox(
  conversationId: string,
  deliveredClientIds: Iterable<string>
) {
  const delivered = new Set(deliveredClientIds);
  const entries = store.get(outboxAtom);
  if (
    entries.some(
      (entry) =>
        entry.conversationId === conversationId && delivered.has(entry.clientId)
    )
  ) {
    setEntries((list) =>
      list.filter((entry) => !delivered.has(entry.clientId))
    );
  }
}

/** The service worker sent this one while the app wasn't looking (D68). */
export function outboxSentElsewhere(clientId: string) {
  const entries = store.get(outboxAtom);
  if (entries.some((entry) => entry.clientId === clientId)) {
    store.set(
      outboxAtom,
      entries.filter((entry) => entry.clientId !== clientId)
    );
  }
}

export function onMessageSent(listener: (message: SentMessage) => void) {
  sentListeners.add(listener);
  return () => {
    sentListeners.delete(listener);
  };
}

/** An edit or reaction the server refused when it was replayed. */
export function onChangeRefused(listener: (entry: OutboxEntry) => void) {
  refusedListeners.add(listener);
  // Any the service worker found refused before anyone listened.
  reportRefusedChanges();
  return () => {
    refusedListeners.delete(listener);
  };
}

/** The access token the service worker sends with (D68). */
export function shareOutboxAuth(
  auth: { userId: string; accessToken: string } | null
) {
  void writeAuth(auth);
}

// M23 kept the outbox in localStorage; move it over once (D66).
function takeLegacy(userId: string): OutboxEntry[] {
  try {
    const raw = localStorage.getItem(LEGACY_PREFIX + userId);
    if (!raw) return [];
    localStorage.removeItem(LEGACY_PREFIX + userId);
    return (JSON.parse(raw) as OutboxEntry[]).map((entry) => ({
      ...entry,
      kind: entry.kind ?? 'send',
    }));
  } catch {
    return [];
  }
}

/**
 * Switch to a user's outbox (D34): load what they left unsent on this device.
 * Signing out (null) forgets the previous user's unsent messages.
 */
export function loadOutbox(userId: string | null): Promise<void> {
  if (userId === ownerId) return loading;
  const previous = ownerId;
  ownerId = userId;
  store.set(outboxAtom, []);
  loading = (async () => {
    if (previous && userId === null) {
      await writeEntries(previous, []);
      await writeAuth(null);
    }
    if (!userId) return;
    const legacy = takeLegacy(userId);
    const stored = await readEntries(userId);
    if (ownerId !== userId) return;
    const known = new Set(stored.map((entry) => entry.clientId));
    const entries = [
      ...stored,
      ...legacy.filter((entry) => !known.has(entry.clientId)),
    ].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    // Anything queued while loading stays after what was waiting.
    const queued = store.get(outboxAtom);
    store.set(outboxAtom, [...entries, ...queued]);
    if (legacy.length > 0 || queued.length > 0) {
      await writeEntries(userId, store.get(outboxAtom));
    }
  })();
  return loading;
}
